/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageProps } from "../../../models/PageProps";
import { getGeneralError, hasAuthority } from "../../../util/helperFunctions";
import { Loader } from "../../../components/Loader";
import { strings } from "../../../common/Strings/Strings";
import AlertBox from "../../../components/AlertBox";
import Tabs from "../../../components/Tabs/Tabs";
import { ClinicDetailsResponse } from "../../../models/management/ClinicDetailsResponse";
import ClinicManagementApi from "../../../api/ClinicManagementApi";
import ClinicLogo from "../../../components/Pictures/Clinic/ClinicLogo";
import ClinicBasicInfoTab from "./ClinicBasicInfoTab";
import AnalyticsEventsTable from "../Analytics/AnalyticsEventsTable";
import ClinicBillingTab from "./ClinicBillingTab";
import { useClinic } from "../../../contexts/ClinicContext";
import { LoggedInUser, setRoleOfActiveUser, updateLoggedInUser } from "../../../util/LocalStorageVariables";
import { SiteManagerAuthority } from "../../../models/management/SiteManagerAuthority";
import ClinicDataExportModal from "./ClinicDataExportModal";
import Button from "../../../components/Button";
import Dropdown from "../../../components/Dropdown/Dropdown";
import DeleteClinicModal from "./DeleteClinicModal";
import ChangeVatModal from "./ChangeVatModal";

enum ClinicDetailsTabs {
  basic = "basic",
  events = "events",
  billing = "billing",
}

type Params = {
  clinicId: string;
};

const ClinicDetailsPage: React.FC<PageProps> = (props: PageProps) => {
  const { setPageLoading } = props;
  const { clinicId } = useParams<Params>();
  const { clearClinic, fetchClinic } = useClinic();
  const navigate = useNavigate();

  const [clinic, setClinic] = useState<ClinicDetailsResponse>();
  const [key, setKey] = useState<ClinicDetailsTabs>(ClinicDetailsTabs.basic);
  const [error, setError] = useState<string | null>(null);
  const [isExportModalVisible, setExportModalVisible] = useState<boolean>(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [isVatModalVisible, setVatModalVisible] = useState<boolean>(false);

  const getClinicDetails = async (id: string) => {
    try {
      setPageLoading(true);
      const resp = await ClinicManagementApi.getClinicDetails(id);
      setClinic(resp.data);
      setError(null);
    } catch (e) {
      setError(await getGeneralError(e));
    } finally {
      setPageLoading(false);
    }
  };

  const accessClinic = async () => {
    if (!clinicId) return;
    setPageLoading(true);
    clearClinic();
    setRoleOfActiveUser("doctor");
    updateLoggedInUser((u: LoggedInUser) => ({
      ...u,
      activeClinicId: undefined,
    }));
    await fetchClinic(clinicId);
    navigate("/");
  };

  useEffect(() => {
    if (clinicId) {
      void getClinicDetails(clinicId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId]);

  if (!clinic) {
    return <></>;
  }

  if (error) {
    return <AlertBox message={error} />;
  }

  return (
    <main className="main-signed-in">
      <section>
        <div className="px-4 lg:px-6 py-6 space-y-6">
          <div className="flex flex-wrap items-center">
            <div style={{ width: "100px", height: "100px" }}>
              <ClinicLogo clinicId={clinic.id} logoPictureId={clinic.logoPictureId} />
            </div>
            <div className="text-xl font-bold lg:text-2xl text-zinc-800 dark:text-white ml-3">{clinic.name}</div>
            <div
              className="ml-auto flex space-x-3"
              hidden={!hasAuthority(SiteManagerAuthority.MANAGER_CLINIC_REMOTE_ACCESS)}
            >
              <Button onClick={accessClinic}>{strings.remoteAccess}</Button>
              <Dropdown className="min-w-48">
                <Dropdown.Toggle />
                <Dropdown.Content>
                  <Button className="dropdown-item" onClick={() => setExportModalVisible(true)} variant="link">
                    {strings.exportClinicData}
                  </Button>
                  <Button className="dropdown-item" onClick={() => setVatModalVisible(true)} variant="link">
                    {strings.changeVatNumber}
                  </Button>
                  <Button className="dropdown-item" onClick={() => setDeleteModalOpen(true)} variant="link">
                    {strings.deleteClinic}
                  </Button>
                </Dropdown.Content>
              </Dropdown>
            </div>
          </div>
          <Tabs
            tabs={[
              { title: strings.mainPage, tab: ClinicDetailsTabs.basic },
              { title: strings.activityLogs, tab: ClinicDetailsTabs.events },
              { title: strings.billing, tab: ClinicDetailsTabs.billing },
            ]}
            currentTabKey={key}
            setTab={setKey}
          />
          <AlertBox message={error} />

          {key === ClinicDetailsTabs.basic && <ClinicBasicInfoTab clinic={clinic} />}

          {key === ClinicDetailsTabs.events && <AnalyticsEventsTable clinicId={clinic.id} />}

          {key === ClinicDetailsTabs.billing && <ClinicBillingTab clinic={clinic} />}
        </div>
      </section>
      <ClinicDataExportModal
        isVisible={isExportModalVisible}
        onClose={() => setExportModalVisible(false)}
        clinic={clinic}
      />
      <ChangeVatModal
        clinic={clinic}
        isOpen={isVatModalVisible}
        close={() => {
          setVatModalVisible(false);
          if (clinicId) void getClinicDetails(clinicId);
        }}
      />
      <DeleteClinicModal clinic={clinic} isOpen={isDeleteModalOpen} close={() => setDeleteModalOpen(false)} />
    </main>
  );
};

export default Loader(ClinicDetailsPage);
